import { defineStore } from 'pinia'
import { noAuthApi } from '@dashboard/services/axiosApi'
import { ref } from 'vue'

type VersionPayload = {
  deployed_at: number | null
  frontend_build_id: string | null
  maintenance: boolean
  maintenanceMessage?: string | null
  maintenanceEta?: string | null
}

type MaintenancePayload = { active: boolean; message?: string; estimatedEnd?: string }

interface SystemChannel {
  listen(event: 'AppVersionChanged', callback: (data: VersionPayload) => void): SystemChannel
  listen(event: 'MaintenanceModeChanged', callback: (data: MaintenancePayload) => void): SystemChannel
  subscribed(callback: () => void): SystemChannel
}

declare const Echo: {
  channel: (channel: string) => SystemChannel
}

const MODULE_LOAD_FAILURES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed'
]

export function isModuleLoadFailure(reason: unknown): boolean {
  const message = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : ''
  return MODULE_LOAD_FAILURES.some((failure) => message.includes(failure))
}

export const useSystemStore = defineStore('system', () => {
  const newVersionAvailable = ref(false)
  const maintenanceMode = ref(false)
  const maintenanceMessage = ref<string | null>(null)
  const maintenanceEta = ref<string | null>(null)
  const targetBuildId = ref<string | null>(null)

  const currentBuildId = window.__RIGHTMESSAGE_FRONTEND_BUILD_ID__
  let systemSnapshotRequest: Promise<void> | null = null
  let systemSnapshotQueued = false
  let systemEventSequence = 0

  function setMaintenance(active: boolean, message?: string | null, eta?: string | null) {
    maintenanceMode.value = active
    maintenanceMessage.value = message ?? null
    maintenanceEta.value = eta ?? null

    // If maintenance just ended and a new version is available, reload
    if (!active && newVersionAvailable.value) {
      reloadApp()
    }
  }

  function applyVersion(data: VersionPayload) {
    if (data.frontend_build_id && data.frontend_build_id !== currentBuildId) {
      targetBuildId.value = data.frontend_build_id
      newVersionAvailable.value = true
    }
  }

  function markSystemEvent() {
    systemEventSequence += 1
    if (systemSnapshotRequest) {
      systemSnapshotQueued = true
    }
  }

  function initSystemChannel() {
    Echo.channel('rm-system')
      .listen('AppVersionChanged', (data: VersionPayload) => {
        markSystemEvent()
        applyVersion(data)
      })
      .listen('MaintenanceModeChanged', (data: MaintenancePayload) => {
        markSystemEvent()
        setMaintenance(data.active, data.message, data.estimatedEnd)
      })
      .subscribed(() => {
        void reconcileSystemSnapshot()
      })
  }

  async function fetchVersion() {
    const eventSequenceAtRequest = systemEventSequence
    const { data } = await noAuthApi.get<VersionPayload>('/api/version')
    if (eventSequenceAtRequest !== systemEventSequence) {
      return
    }

    applyVersion(data)
    if (data.maintenance) {
      setMaintenance(true, data.maintenanceMessage, data.maintenanceEta)
    } else if (maintenanceMode.value) {
      setMaintenance(false)
    }
  }

  function reconcileSystemSnapshot(): Promise<void> {
    if (systemSnapshotRequest) {
      systemSnapshotQueued = true
      return systemSnapshotRequest
    }

    systemSnapshotRequest = (async () => {
      do {
        systemSnapshotQueued = false
        await fetchVersion()
      } while (systemSnapshotQueued)
    })().finally(() => {
      systemSnapshotRequest = null
    })

    return systemSnapshotRequest
  }

  async function handleAssetLoadFailure() {
    try {
      await reconcileSystemSnapshot()
    } finally {
      newVersionAvailable.value = true
    }
  }

  function reloadApp() {
    const target = targetBuildId.value
    if (target === currentBuildId) {
      return
    }

    const guardTarget = target ?? 'latest'
    const guard = `rm:release-reload:${currentBuildId ?? 'unknown'}:${guardTarget}`
    if (sessionStorage.getItem(guard)) {
      return
    }
    sessionStorage.setItem(guard, '1')

    const url = new URL(window.location.href)
    if (target) {
      url.searchParams.set('rm_build', target)
    }
    window.location.replace(url)
  }

  return {
    newVersionAvailable,
    targetBuildId,
    maintenanceMode,
    maintenanceMessage,
    maintenanceEta,
    setMaintenance,
    initSystemChannel,
    handleAssetLoadFailure,
    reloadApp
  }
})
